// ONE rule for the expiry status of a certificate / document (ship documents, crew certificates), used by the dashboard tiles and
// "Needs you", the lists and their filters (All / Valid / Expiring soon / Expired), the alerts / watchers and the AI tools:
//   - a certificate is valid THROUGH its expiry date, in the SHIP's time zone (config.timezone: shipToday / shipTodayAuto)
//   - expired only from the next day
//   - on the day: "Expires today" (amber), counted as expiring soon
//   - expiring soon = today .. today + 60 days;  no date = "No expiry"
// The stored documents.status / crew_docs.status column is NOT used to decide: it is only written when a record is saved, so it
// goes stale (the dashboard said "1 expired" while the list showed 4). The database side: sql/docs-ux.sql (hc_expiry_run).
// Pure: no React, no clock unless asked (today = 'YYYY-MM-DD'). tests/expiry.test.mjs.
import { shipToday, shipTodayAuto } from './shipTime.js'

export const SOON_DAYS = 60

// a stored date ('YYYY-MM-DD...', 'DD/MM/YYYY', { value }) -> 'YYYY-MM-DD' or ''
export function isoDay(v) {
  const s = String(v && typeof v === 'object' && !Array.isArray(v) ? (v.value ?? '') : (v ?? '')).trim()
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (m) return m[1] + '-' + m[2] + '-' + m[3]
  m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s)
  return m ? m[3] + '-' + m[2] + '-' + m[1] : ''
}
const dayNum = iso => { const [y, m, d] = iso.split('-').map(Number); return Math.round(Date.UTC(y, m - 1, d) / 86400000) }

// whole days from today to the expiry date: 0 = expires today, 1 = tomorrow, -1 = expired yesterday; null = no date
export function daysLeft(expiry, today) {
  const e = isoDay(expiry), t = isoDay(today)
  return e && t ? dayNum(e) - dayNum(t) : null
}

// the ship's date today (tz = the vessel's IANA zone; none = the zone the app registered, else the browser's)
export const expiryToday = (tz, now) => (tz ? shipToday(tz, now) : shipTodayAuto(now))

export const EXPIRY_LABEL = { expired: 'Expired', today: 'Expires today', expiring: 'Expiring soon', valid: 'Valid', none: 'No expiry' }
const TONE = { expired: 'r', today: 'a', expiring: 'a', valid: 'g', none: 'x' }

// -> { key: expired | today | expiring | valid | none, chip: the filter it is counted under (today -> expiring), label, tone
//      (r / a / g / x), days (daysLeft), expired, soon (today or within soonDays) }
export function expiryStatus(expiry, today, { soonDays = SOON_DAYS } = {}) {
  const days = daysLeft(expiry, today)
  const key = days == null ? 'none' : days < 0 ? 'expired' : days === 0 ? 'today' : days <= soonDays ? 'expiring' : 'valid'
  return { key, chip: key === 'today' ? 'expiring' : key, label: EXPIRY_LABEL[key], tone: TONE[key], days, expired: key === 'expired', soon: key === 'today' || key === 'expiring' }
}

// rows -> { all, expired, expiring (incl. today), today, valid, none }; dateOf(row) gives the row's expiry date
export function expiryCounts(rows, dateOf, today, opts) {
  const out = { all: 0, expired: 0, expiring: 0, today: 0, valid: 0, none: 0 }
  for (const r of rows || []) {
    const s = expiryStatus(dateOf(r), today, opts)
    out.all++; out[s.chip]++; if (s.key === 'today') out.today++
  }
  return out
}

// "expired 3 days ago" / "expires today" / "expires tomorrow" / "expires in 12 days" / ''
export function expiryText(days) {
  if (days == null) return ''
  const p = n => n + ' ' + (n === 1 ? 'day' : 'days')
  return days < 0 ? 'expired ' + p(-days) + ' ago' : days === 0 ? 'expires today' : days === 1 ? 'expires tomorrow' : 'expires in ' + p(days)
}

// the value the record's status column gets when it is saved (kept for old readers / exports; nothing decides on it any more).
// Ship documents: 'Expired' / 'Valid' / 'No expiry'; crew certificates: { soon: 'Expiring Soon', none: 'Valid' } as they always stored
export function storedStatus(expiry, today, { soon = null, none = 'No expiry' } = {}) {
  const s = expiryStatus(expiry, today)
  return s.key === 'none' ? none : s.expired ? 'Expired' : (soon && s.soon) ? soon : 'Valid'
}
